import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPin,
  Bus,
  User,
  Route,
  Save,
  AlertCircle,
  CheckCircle,
  X,
  Tag,
  Hash,
  Activity,
  Users,
  Home,
  Sunrise,
  Sunset,
} from 'lucide-react';
import logger from '@/utils/logger';

const CircuitControlPanel = ({
  selectedStops = [],
  optimizedOrder = [],
  inboundOrder = [],
  buses = [],
  drivers = [],
  selectedBus,
  selectedDriver,
  busParksAtHome = false,
  circuitName,
  circuitDescription,
  circuitCode = '',
  circuitStatus = 'active',
  isCreating,
  editingRouteId = null,
  onOpenOrderingModal,
  onBusChange,
  onDriverChange,
  onBusParksAtHomeChange,
  onNameChange,
  onDescriptionChange,
  onCodeChange,
  onStatusChange,
  onCreateCircuit,
}) => {
  const selectedStopsWithoutSchool = selectedStops.filter((stop) => !stop.isSchool);
  const nonSchoolStops = optimizedOrder.filter((s) => !s.isSchool);
  const totalStudents = nonSchoolStops.reduce((sum, s) => sum + (s.student_count || 0), 0);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmStopDirection, setConfirmStopDirection] = useState('inbound');

  const previousStatus = useRef(circuitStatus);

  useEffect(() => {
    if (previousStatus.current === 'active' && circuitStatus !== 'active') {
      logger.debug('Désassignation bus/chauffeur sur changement statut active →', circuitStatus);
      onBusChange('');
      onDriverChange('');
    }
    previousStatus.current = circuitStatus;
  }, [circuitStatus, onBusChange, onDriverChange]);

  const isValid =
    circuitStatus === 'active'
      ? circuitName.trim() && selectedStopsWithoutSchool.length > 0 && selectedBus && selectedDriver
      : circuitName.trim() && selectedStopsWithoutSchool.length > 0;

  // Bus disponibles : seulement actifs ET sans circuit assigné (ou le bus déjà sélectionné)
  // Utilise hasAssignedRoute (enrichi depuis routes) ou assigned_route_id/assigned_route en fallback
  const availableBuses = buses.filter((bus) => {
    // Si c'est le bus déjà sélectionné, le garder
    if (bus.id === selectedBus) return true;
    // Vérifier qu'il n'a pas de circuit assigné (hasAssignedRoute est enrichi depuis routes)
    const hasRoute = bus.hasAssignedRoute === true || bus.assigned_route_id || bus.assigned_route;
    return !hasRoute;
  });

  // Chauffeurs disponibles : seulement actifs ET sans circuit assigné (ou le chauffeur déjà sélectionné)
  // Utilise hasAssignedRoute (enrichi depuis routes) ou assigned_route_id/assigned_route en fallback
  const availableDrivers = drivers.filter((driver) => {
    // Si c'est le chauffeur déjà sélectionné, le garder
    if (driver.id === selectedDriver) return true;
    // Vérifier qu'il n'a pas de circuit assigné (hasAssignedRoute est enrichi depuis routes)
    const hasRoute = driver.hasAssignedRoute === true || driver.assigned_route_id || driver.assigned_route;
    return !hasRoute;
  });

  // Obtenir les informations du bus et du chauffeur sélectionnés
  const selectedBusData = buses.find((bus) => bus.id === selectedBus);
  const selectedDriverData = drivers.find((driver) => driver.id === selectedDriver);

  // Gestionnaire pour ouvrir la popup de confirmation
  const handleValidateClick = () => {
    if (isValid) {
      setShowConfirmModal(true);
    }
  };

  // Gestionnaire pour créer le circuit depuis la popup
  const handleCreateFromModal = () => {
    setShowConfirmModal(false);
    onCreateCircuit();
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center space-x-2 mb-2">
          <Route className="text-blue-600" size={20} />
          <h2 className="font-semibold text-gray-900">{editingRouteId ? 'Modifier le Circuit' : 'Nouveau Circuit'}</h2>
        </div>
        <p className="text-sm text-gray-600">
          {editingRouteId ? 'Modifiez votre circuit étape par étape' : 'Configurez votre circuit étape par étape'}
        </p>
      </div>

      {/* Contenu scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Étape 1: Sélection des arrêts */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                selectedStopsWithoutSchool.length > 0 ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
              }`}
            >
              1
            </div>
            <h3 className="font-medium text-gray-900">Sélection des Arrêts</h3>
          </div>

          <div className="ml-8 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Arrêts sélectionnés:</span>
              <span
                className={`font-medium ${selectedStopsWithoutSchool.length > 0 ? 'text-green-600' : 'text-gray-400'}`}
              >
                {selectedStopsWithoutSchool.length}
              </span>
            </div>
            <button
              type="button"
              onClick={onOpenOrderingModal}
              disabled={selectedStopsWithoutSchool.length === 0}
              className={`w-full rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                selectedStopsWithoutSchool.length > 0
                  ? 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              Ordonner les arrêts
            </button>

            {selectedStops.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                <AnimatePresence>
                  {inboundOrder.map((stop, index) => {
                    const stopNumber = stop.isSchool
                      ? null
                      : inboundOrder.slice(0, index + 1).filter((s) => !s.isSchool).length;
                    return (
                      <motion.div
                        key={stop.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className="flex items-center space-x-2 py-1"
                      >
                        <div
                          className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                            stop.isSchool ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'
                          }`}
                        >
                          {stop.isSchool ? <Home size={10} /> : stopNumber}
                        </div>
                        <span
                          className={`text-sm truncate ${
                            stop.isSchool ? 'text-orange-700 font-medium' : 'text-gray-700'
                          }`}
                        >
                          {stop.name || stop.stop_name || 'Arrêt'}
                        </span>
                        {!stop.isSchool && stop.student_count > 0 && (
                          <span className="text-xs text-blue-600 bg-blue-100 px-1 rounded">{stop.student_count}</span>
                        )}
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}

            {selectedStopsWithoutSchool.length === 0 && (
              <div className="text-sm text-gray-500 italic">
                Cliquez sur les arrêts sur la carte pour les sélectionner
              </div>
            )}
          </div>
        </div>

        {/* Étape 2: Informations du circuit */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                circuitName.trim() ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
              }`}
            >
              2
            </div>
            <h3 className="font-medium text-gray-900">Informations du Circuit</h3>
          </div>

          <div className="ml-8 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom du circuit *</label>
              <input
                type="text"
                value={circuitName}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="Ex: Circuit Centre-Ville"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code circuit</label>
              <input
                type="text"
                value={circuitCode}
                onChange={(e) => onCodeChange(e.target.value)}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut *</label>
              <select
                value={circuitStatus}
                onChange={(e) => onStatusChange?.(e.target.value)}
                disabled={!editingRouteId} // Grisé en mode création, actif en mode édition
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description (optionnel)</label>
              <textarea
                value={circuitDescription}
                onChange={(e) => onDescriptionChange(e.target.value)}
                placeholder="Description du circuit..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
              />
            </div>
          </div>
        </div>

        {/* Étape 3: Assignations */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                selectedBus && selectedDriver ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
              }`}
            >
              3
            </div>
            <h3 className="font-medium text-gray-900">Assignations</h3>
          </div>

          <div className="ml-8 space-y-3">
            {/* Sélection du bus */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Bus size={14} className="inline mr-1" />
                Bus {circuitStatus === 'active' && <span className="text-red-500">*</span>}
                {circuitStatus !== 'active' && <span className="text-gray-500 text-xs">(optionnel)</span>}
              </label>
              <select
                value={selectedBus}
                onChange={(e) => onBusChange(e.target.value)}
                required={circuitStatus === 'active'}
                disabled={circuitStatus !== 'active'}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                  circuitStatus !== 'active' ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                }`}
              >
                <option value="">Sélectionner un bus...</option>
                {availableBuses.map((bus) => (
                  <option key={bus.id} value={bus.id}>
                    {bus.bus_number || bus.license_plate}
                    {bus.capacity && ` (${bus.capacity} places)`}
                  </option>
                ))}
              </select>
              {availableBuses.length === 0 && <p className="text-xs text-orange-600 mt-1">Aucun bus disponible</p>}
            </div>

            {/* Sélection du chauffeur */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <User size={14} className="inline mr-1" />
                Chauffeur {circuitStatus === 'active' && <span className="text-red-500">*</span>}
                {circuitStatus !== 'active' && <span className="text-gray-500 text-xs">(optionnel)</span>}
              </label>
              <select
                value={selectedDriver}
                onChange={(e) => onDriverChange(e.target.value)}
                required={circuitStatus === 'active'}
                disabled={circuitStatus !== 'active'}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                  circuitStatus !== 'active' ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                }`}
              >
                <option value="">Sélectionner un chauffeur...</option>
                {availableDrivers.map((driver) => (
                  <option key={driver.id} value={driver.id}>
                    {driver.first_name} {driver.last_name}
                  </option>
                ))}
              </select>
              {availableDrivers.length === 0 && (
                <p className="text-xs text-orange-600 mt-1">Aucun chauffeur disponible</p>
              )}
            </div>

            {/* Option: bus garé au domicile du chauffeur */}
            <div
              className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                busParksAtHome ? 'bg-indigo-50 border-indigo-300' : 'bg-gray-50 border-gray-200 hover:border-gray-300'
              } ${circuitStatus !== 'active' ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={() => circuitStatus === 'active' && onBusParksAtHomeChange?.(!busParksAtHome)}
            >
              <div className={`mt-0.5 flex-shrink-0 ${busParksAtHome ? 'text-indigo-600' : 'text-gray-400'}`}>
                <Home size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Le chauffeur gare le bus à son domicile</span>
                  <div
                    className={`w-9 h-5 rounded-full transition-colors flex-shrink-0 ml-2 ${
                      busParksAtHome ? 'bg-indigo-600' : 'bg-gray-300'
                    }`}
                  >
                    <div
                      className={`w-4 h-4 bg-white rounded-full shadow mt-0.5 transition-transform ${
                        busParksAtHome ? 'translate-x-4' : 'translate-x-0.5'
                      }`}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Le trajet démarrera quand le bus approchera le premier arrêt
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer avec bouton de création */}
      <div className="p-4 border-t bg-gray-50">
        {/* Validation */}
        <div className="mb-3">
          {isValid ? (
            <div className="flex items-center space-x-2 text-green-600 text-sm">
              <CheckCircle size={16} />
              <span>Circuit prêt à être créé</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2 text-orange-600 text-sm">
              <AlertCircle size={16} />
              <span>
                {selectedStopsWithoutSchool.length === 0
                  ? 'Sélectionnez des arrêts.'
                  : !circuitName.trim()
                    ? 'Nom requis.'
                    : circuitStatus === 'active' && (!selectedBus || !selectedDriver)
                      ? 'Assignation requise (bus et chauffeur pour circuit actif).'
                      : ''}
              </span>
            </div>
          )}
        </div>

        {/* Bouton de validation */}
        <button
          onClick={handleValidateClick}
          disabled={!isValid || isCreating}
          className={`w-full flex items-center justify-center space-x-2 py-3 rounded-lg font-medium transition-all ${
            isValid && !isCreating
              ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          <Save size={16} />
          <span>Valider le Circuit</span>
        </button>
      </div>

      {/* Popup de confirmation */}
      <AnimatePresence>
        {showConfirmModal && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowConfirmModal(false)}
              className="fixed inset-0 bg-black bg-opacity-50 z-50"
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Route className="text-blue-600" size={24} />
                      <h2 className="text-xl font-semibold text-gray-900">
                        {editingRouteId
                          ? 'Confirmation de modification du circuit'
                          : 'Confirmation de création du circuit'}
                      </h2>
                    </div>
                    <button
                      onClick={() => setShowConfirmModal(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    {editingRouteId
                      ? 'Vérifiez les informations avant de modifier le circuit'
                      : 'Vérifiez les informations avant de créer le circuit'}
                  </p>
                </div>

                {/* Contenu scrollable */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {/* Informations du circuit et Assignations côte à côte */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Informations générales */}
                    <div className="space-y-3">
                      <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
                        <Route size={18} />
                        <span>Informations du circuit</span>
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 flex items-center space-x-2">
                            <Tag size={16} />
                            <span>Nom:</span>
                          </span>
                          <span className="font-medium text-gray-900">{circuitName}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 flex items-center space-x-2">
                            <Hash size={16} />
                            <span>Code:</span>
                          </span>
                          <span className="font-medium text-gray-900">{circuitCode || 'Non défini'}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 flex items-center space-x-2">
                            <Activity size={16} />
                            <span>Statut:</span>
                          </span>
                          <span
                            className={`font-medium px-2 py-1 rounded text-xs ${
                              circuitStatus === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {circuitStatus === 'active' ? 'Actif' : 'Inactif'}
                          </span>
                        </div>
                        {circuitDescription && (
                          <div className="flex flex-col">
                            <span className="text-gray-600 mb-1">Description:</span>
                            <span className="text-gray-900 text-sm">{circuitDescription}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Assignations */}
                    <div className="space-y-3">
                      <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
                        <User size={18} />
                        <span>Assignations</span>
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 flex items-center space-x-2">
                            <Bus size={16} />
                            <span>Bus:</span>
                          </span>
                          <span className="font-medium text-gray-900">
                            {selectedBusData
                              ? selectedBusData.bus_number || selectedBusData.license_plate || 'N/A'
                              : 'Aucun bus assigné'}
                          </span>
                        </div>
                        {selectedBusData?.capacity != null && (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600 flex items-center space-x-2">
                              <Users size={16} />
                              <span>Capacité:</span>
                            </span>
                            <span
                              className={`font-medium ${totalStudents > selectedBusData.capacity ? 'text-red-600' : 'text-gray-900'}`}
                            >
                              {selectedBusData.capacity} place
                              {selectedBusData.capacity > 1 ? 's' : ''}
                              {totalStudents > 0 && ` · ${totalStudents} élève${totalStudents > 1 ? 's' : ''}`}
                            </span>
                          </div>
                        )}
                        {selectedBusData?.capacity != null && totalStudents > selectedBusData.capacity && (
                          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">
                            <AlertCircle size={13} className="shrink-0 mt-0.5" />
                            <span>
                              Capacité insuffisante — {totalStudents - selectedBusData.capacity} élève
                              {totalStudents - selectedBusData.capacity > 1 ? 's' : ''} en trop.
                            </span>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 flex items-center space-x-2">
                            <User size={16} />
                            <span>Chauffeur:</span>
                          </span>
                          <span className="font-medium text-gray-900">
                            {selectedDriverData
                              ? `${selectedDriverData.first_name} ${selectedDriverData.last_name}`
                              : 'Aucun chauffeur assigné'}
                          </span>
                        </div>
                        {busParksAtHome && (
                          <div className="flex items-center gap-2 text-indigo-700 bg-indigo-50 rounded-lg px-3 py-2 text-sm">
                            <Home size={14} className="shrink-0" />
                            <span>Bus garé chez le chauffeur</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Liste des arrêts avec toggle Matin/Soir */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
                        <MapPin size={18} />
                        <span>Arrêts ({nonSchoolStops.length})</span>
                      </h3>
                      <div
                        className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 text-xs"
                        role="group"
                      >
                        <button
                          type="button"
                          onClick={() => setConfirmStopDirection('inbound')}
                          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md font-medium transition-all ${confirmStopDirection === 'inbound' ? 'bg-white text-blue-700 shadow-sm ring-1 ring-blue-200/60' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                          <Sunrise size={12} />
                          Matin
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmStopDirection('outbound')}
                          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md font-medium transition-all ${confirmStopDirection === 'outbound' ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-indigo-200/60' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                          <Sunset size={12} />
                          Soir
                        </button>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                      <div className="space-y-2">
                        {(() => {
                          const activeOrder = confirmStopDirection === 'inbound' ? inboundOrder : optimizedOrder;
                          return activeOrder.map((stop, index) => {
                            const stopNumber = stop.isSchool
                              ? null
                              : activeOrder.slice(0, index + 1).filter((s) => !s.isSchool).length;
                            return (
                              <div
                                key={stop.id}
                                className={`flex items-center space-x-3 p-2 bg-white rounded border ${
                                  stop.isSchool ? 'border-orange-200 bg-orange-50' : 'border-gray-200'
                                }`}
                              >
                                <div
                                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                    stop.isSchool ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'
                                  }`}
                                >
                                  {stop.isSchool ? <Home size={10} /> : stopNumber}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div
                                    className={`font-medium text-sm truncate ${stop.isSchool ? 'text-orange-900' : 'text-gray-900'}`}
                                  >
                                    {stop.name || stop.stop_name || 'Arrêt'}
                                  </div>
                                  {stop.address && <div className="text-xs text-gray-500 truncate">{stop.address}</div>}
                                </div>
                                {!stop.isSchool && stop.student_count > 0 && (
                                  <div className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded flex-shrink-0">
                                    {stop.student_count} élève
                                    {stop.student_count > 1 ? 's' : ''}
                                  </div>
                                )}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer avec boutons */}
                <div className="p-6 border-t bg-gray-50 flex items-center justify-end space-x-3">
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleCreateFromModal}
                    disabled={isCreating}
                    className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center space-x-2 ${
                      isCreating
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                    }`}
                  >
                    {isCreating ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Création en cours...</span>
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        <span>{editingRouteId ? 'Modifier le circuit' : 'Créer le circuit'}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CircuitControlPanel;
